import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Heart, TrendingUp, Users, Calendar, Award, AlertTriangle, ThumbsUp } from "lucide-react";
import axios from "axios";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Mock retention data
const retentionCohorts = [
  { month: "Jul", retained: 95, churned: 5 },
  { month: "Aug", retained: 94, churned: 6 },
  { month: "Sep", retained: 96, churned: 4 },
  { month: "Oct", retained: 93, churned: 7 },
  { month: "Nov", retained: 95, churned: 5 },
  { month: "Dec", retained: 94, churned: 6 },
];

const engagementData = [
  { name: "High Engagement", value: 180 },
  { name: "Medium Engagement", value: 85 },
  { name: "Low Engagement", value: 35 },
];

export default function AdminRetention() {
  const [therapists, setTherapists] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API}/admin/therapists`, { withCredentials: true });
        setTherapists(response.data);
      } catch (error) {
        console.error("Error:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const activeCount = therapists.filter(t => t.status === "active").length;
  const avgTenure = 8.5; // Mock: months
  const repeatPatientRate = 72; // Mock: percentage

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-retention">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-primary-50 rounded-lg flex items-center justify-center">
                <Heart className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Retention Rate</p>
                <p className="text-2xl font-heading font-bold text-slate-900">94.2%</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-emerald-50 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Active Therapists</p>
                <p className="text-2xl font-heading font-bold text-slate-900">{activeCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center">
                <Calendar className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Avg. Tenure</p>
                <p className="text-2xl font-heading font-bold text-slate-900">{avgTenure} months</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-amber-50 rounded-lg flex items-center justify-center">
                <ThumbsUp className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Repeat Patient Rate</p>
                <p className="text-2xl font-heading font-bold text-slate-900">{repeatPatientRate}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Retention Trend */}
        <Card className="chart-container">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Retention Cohorts (%)</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={retentionCohorts}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="month" />
                <YAxis domain={[80, 100]} />
                <Tooltip 
                  formatter={(value) => [`${value}%`, '']}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                />
                <Line 
                  type="monotone" 
                  dataKey="retained" 
                  stroke="#10B981" 
                  strokeWidth={3}
                  dot={{ fill: '#10B981', strokeWidth: 2, r: 4 }}
                  name="Retained"
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Engagement Distribution */}
        <Card className="chart-container">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Engagement Levels</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={engagementData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip 
                  formatter={(value) => [value, 'Therapists']}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                />
                <Bar dataKey="value" fill="#F40d07" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Recognition & Badges */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Award className="w-5 h-5 text-amber-500" />
            Recognition & Achievements
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="p-4 bg-amber-50 rounded-lg text-center">
              <Award className="w-10 h-10 mx-auto mb-2 text-amber-500" />
              <p className="font-heading font-semibold text-slate-900">Top Performer</p>
              <p className="text-sm text-slate-600">12 therapists</p>
            </div>
            <div className="p-4 bg-emerald-50 rounded-lg text-center">
              <Heart className="w-10 h-10 mx-auto mb-2 text-emerald-500" />
              <p className="font-heading font-semibold text-slate-900">Patient Favorite</p>
              <p className="text-sm text-slate-600">25 therapists</p>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg text-center">
              <TrendingUp className="w-10 h-10 mx-auto mb-2 text-blue-500" />
              <p className="font-heading font-semibold text-slate-900">Rising Star</p>
              <p className="text-sm text-slate-600">8 therapists</p>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg text-center">
              <Calendar className="w-10 h-10 mx-auto mb-2 text-purple-500" />
              <p className="font-heading font-semibold text-slate-900">Consistency King</p>
              <p className="text-sm text-slate-600">15 therapists</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Engagement Table */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg">Therapist Engagement</CardTitle>
        </CardHeader>
        <CardContent>
          <Table className="data-table">
            <TableHeader>
              <TableRow>
                <TableHead>Therapist</TableHead>
                <TableHead>Active Months</TableHead>
                <TableHead>Engagement Score</TableHead>
                <TableHead>Earnings Growth</TableHead>
                <TableHead>Repeat Patients</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {therapists.slice(0, 8).map((th, i) => {
                const engagement = th.performance_score >= 80 ? "High" : th.performance_score >= 60 ? "Medium" : "Low";
                const activeMonths = Math.floor(Math.random() * 18) + 3;
                const earningsGrowth = Math.floor(Math.random() * 30) + 5;
                const repeatPatients = Math.floor(Math.random() * 40) + 40;
                
                return (
                  <TableRow key={th.therapist_id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="w-8 h-8">
                          <AvatarImage src={th.picture} />
                          <AvatarFallback className="bg-primary text-white text-sm">
                            {th.name.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="font-medium">{th.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>{activeMonths} months</TableCell>
                    <TableCell>
                      <Badge className={
                        engagement === "High" ? "bg-emerald-100 text-emerald-700" :
                        engagement === "Medium" ? "bg-amber-100 text-amber-700" :
                        "bg-red-100 text-red-700"
                      }>
                        {engagement}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-emerald-600 flex items-center gap-1">
                        <TrendingUp className="w-3 h-3" />
                        +{earningsGrowth}%
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Progress value={repeatPatients} className="w-16 h-2" />
                        <span className="text-sm">{repeatPatients}%</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={th.status === "active" ? "status-active" : "status-pending"}>
                        {th.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
